import React, {useState, useEffect, useCallback} from 'react';
import {View, StyleSheet, FlatList, RefreshControl, TouchableOpacity} from 'react-native';
import {Text, Appbar, Card, ActivityIndicator, Snackbar} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useNavigation, useRoute} from '@react-navigation/native';
import {colors, spacing} from '../../theme';
import {customerApi} from '../../services/api/customerApi';

const formatCurrency = value =>
  '\u20B9' + Number(value || 0).toLocaleString('en-IN');

const formatDate = dateStr => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: '2-digit'});
};

const SummaryCard = ({totalOrdered, totalPaid, currentBalance}) => (
  <Card style={styles.summaryCard}>
    <Card.Content>
      <View style={styles.summaryRow}>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryLabel}>Total Ordered</Text>
          <Text style={[styles.summaryValue, {color: colors.text}]}>
            {formatCurrency(totalOrdered)}
          </Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryLabel}>Total Paid</Text>
          <Text style={[styles.summaryValue, {color: colors.success}]}>
            {formatCurrency(totalPaid)}
          </Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryLabel}>Balance Due</Text>
          <Text style={[styles.summaryValue, {color: currentBalance > 0 ? colors.error : colors.success}]}>
            {formatCurrency(currentBalance)}
          </Text>
        </View>
      </View>
    </Card.Content>
  </Card>
);

const LedgerRow = ({entry}) => {
  const isOrder = entry.type === 'ORDER';
  const isBulk = entry.sourceType === 'BULK_PAYMENT' && entry.items && entry.items.length > 0;
  const [expanded, setExpanded] = useState(false);

  return (
    <View>
      <TouchableOpacity
        activeOpacity={isBulk ? 0.7 : 1}
        onPress={() => isBulk && setExpanded(prev => !prev)}
        style={styles.ledgerRow}>
        <View style={styles.ledgerLeft}>
          <Text style={styles.ledgerDate}>{formatDate(entry.date)}</Text>
        </View>
        <View style={styles.ledgerCenter}>
          <Text style={styles.ledgerDesc} numberOfLines={1}>{entry.description}</Text>
          {entry.orderNumber && isOrder && (
            <Text style={styles.ledgerOrderNum}>{entry.orderNumber}</Text>
          )}
          {isBulk && (
            <View style={styles.bulkTagRow}>
              <View style={styles.bulkTag}>
                <Text style={styles.bulkTagText}>Bulk \u00B7 {entry.items.length} orders</Text>
              </View>
              <Icon
                name={expanded ? 'chevron-up' : 'chevron-down'}
                size={16}
                color={colors.info}
                style={{marginLeft: 4}}
              />
            </View>
          )}
          {entry.sourceType === 'BULK_PAYMENT' && !isBulk && (
            <View style={styles.bulkTag}>
              <Text style={styles.bulkTagText}>Bulk</Text>
            </View>
          )}
        </View>
        <View style={styles.ledgerRight}>
          {isOrder ? (
            <Text style={[styles.ledgerAmount, {color: colors.error}]}>
              {formatCurrency(entry.debit)}
            </Text>
          ) : (
            <Text style={[styles.ledgerAmount, {color: colors.success}]}>
              {formatCurrency(entry.credit)}
            </Text>
          )}
          <Text style={styles.ledgerBalance}>
            Bal: {formatCurrency(entry.balance)}
          </Text>
        </View>
      </TouchableOpacity>
      {isBulk && expanded && (
        <View style={styles.bulkItemsContainer}>
          {entry.items.map((item, idx) => (
            <View key={idx} style={styles.bulkItem}>
              <Text style={styles.bulkItemOrder}>{item.orderNumber}</Text>
              <Text style={styles.bulkItemAmount}>{formatCurrency(item.amount)}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const CustomerLedgerScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const {customerId, customerName} = route.params;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});

  const fetchLedger = useCallback(async () => {
    try {
      const res = await customerApi.getLedger(customerId);
      setLedger(res.data?.data || null);
    } catch (err) {
      setSnackbar({visible: true, message: err.response?.data?.message || 'Failed to load ledger'});
    }
  }, [customerId]);

  useEffect(() => {
    setLoading(true);
    fetchLedger().finally(() => setLoading(false));
  }, [fetchLedger]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchLedger();
    setRefreshing(false);
  }, [fetchLedger]);

  if (loading) {
    return (
      <View style={styles.container}>
        <Appbar.Header style={styles.appbar} dark>
          <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
          <Appbar.Content title="Ledger" titleStyle={styles.appbarTitle} />
        </Appbar.Header>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content
          title="Ledger"
          subtitle={customerName}
          titleStyle={styles.appbarTitle}
          subtitleStyle={styles.appbarSubtitle}
        />
      </Appbar.Header>

      <FlatList
        data={ledger?.entries || []}
        keyExtractor={(item, index) => `${item.type}-${item.orderId}-${index}`}
        ListHeaderComponent={
          ledger && (
            <SummaryCard
              totalOrdered={ledger.totalOrdered}
              totalPaid={ledger.totalPaid}
              currentBalance={ledger.currentBalance}
            />
          )
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No transactions found</Text>
          </View>
        }
        renderItem={({item}) => <LedgerRow entry={item} />}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }
        contentContainerStyle={styles.listContent}
      />

      {ledger?.creditBalance > 0 && (
        <View style={styles.creditBar}>
          <Text style={styles.creditLabel}>Wallet Balance</Text>
          <Text style={styles.creditValue}>{formatCurrency(ledger.creditBalance)}</Text>
        </View>
      )}

      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  appbarSubtitle: {color: colors.white, opacity: 0.8},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  listContent: {paddingBottom: spacing.lg},
  summaryCard: {
    margin: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: 12,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  summaryItem: {alignItems: 'center', flex: 1},
  summaryLabel: {fontSize: 11, color: colors.textSecondary, marginBottom: 4},
  summaryValue: {fontSize: 15, fontWeight: '700'},
  ledgerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
    backgroundColor: colors.surface,
  },
  ledgerLeft: {width: 60},
  ledgerDate: {fontSize: 12, color: colors.textSecondary},
  ledgerCenter: {flex: 1, paddingHorizontal: spacing.sm},
  ledgerDesc: {fontSize: 13, fontWeight: '600', color: colors.text},
  ledgerOrderNum: {fontSize: 11, color: colors.textSecondary, marginTop: 1},
  bulkTagRow: {flexDirection: 'row', alignItems: 'center', marginTop: 3},
  bulkTag: {
    alignSelf: 'flex-start',
    backgroundColor: colors.info + '20',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  bulkTagText: {fontSize: 10, color: colors.info, fontWeight: '600'},
  bulkItemsContainer: {
    backgroundColor: '#F5F8FF',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  bulkItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
    paddingLeft: 60 + spacing.sm,
  },
  bulkItemOrder: {fontSize: 12, color: colors.textSecondary},
  bulkItemAmount: {fontSize: 12, fontWeight: '600', color: colors.success},
  ledgerRight: {alignItems: 'flex-end', minWidth: 90},
  ledgerAmount: {fontSize: 14, fontWeight: '700'},
  ledgerBalance: {fontSize: 10, color: colors.textSecondary, marginTop: 2},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 60},
  emptyText: {color: colors.textSecondary, fontSize: 16},
  creditBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    backgroundColor: colors.info + '10',
    borderTopWidth: 1,
    borderTopColor: colors.divider,
  },
  creditLabel: {fontSize: 13, color: colors.info, fontWeight: '600'},
  creditValue: {fontSize: 14, fontWeight: '700', color: colors.info},
});

export default CustomerLedgerScreen;
